import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "octasker_cart";
const CART_EXPIRY_HOURS = 24;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [couponCode, setCouponCode] = useState(null);
  const [appliedPromotion, setAppliedPromotion] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      try {
        const { items, timestamp, coupon } = JSON.parse(savedCart);
        const savedDate = new Date(timestamp);
        const hoursDiff = (new Date() - savedDate) / (1000 * 60 * 60);
        
        if (hoursDiff < CART_EXPIRY_HOURS && items?.length > 0) {
          setCart(items);
          if (coupon) setCouponCode(coupon);
        } else {
          localStorage.removeItem(CART_STORAGE_KEY);
        }
      } catch (e) {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        items: cart,
        timestamp: new Date().toISOString(),
        coupon: couponCode
      }));
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [cart, couponCode]);

  // Add item to cart
  const addToCart = useCallback((item) => {
    const cartItem = {
      ...item,
      cartId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      addedAt: new Date().toISOString()
    };
    setCart(prev => [...prev, cartItem]);
    return cartItem.cartId;
  }, []);

  // Remove item from cart
  const removeFromCart = useCallback((cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
  }, []);

  // Update item in cart
  const updateCartItem = useCallback((cartId, updates) => {
    setCart(prev => prev.map(item => 
      item.cartId === cartId ? { ...item, ...updates } : item
    ));
  }, []);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCart([]);
    setCouponCode(null);
    setAppliedPromotion(null);
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  // Apply coupon code
  const applyCoupon = useCallback((code, discount) => {
    setCouponCode(code);
    setAppliedPromotion(discount);
  }, []);

  // Remove coupon
  const removeCoupon = useCallback(() => {
    setCouponCode(null);
    setAppliedPromotion(null);
  }, []);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price || 0), 0);
    
    let discount = 0;
    let discountLabel = "";

    // Multi-service discount
    if (cart.length >= 3) {
      discount = subtotal * 0.15;
      discountLabel = "15% Multi-Service Discount (3+ services)";
    } else if (cart.length >= 2) {
      discount = subtotal * 0.10;
      discountLabel = "10% Multi-Service Discount (2 services)";
    }

    // Coupon discount (applied after multi-service)
    let couponDiscount = 0;
    if (appliedPromotion) {
      if (appliedPromotion.type === "percentage") {
        couponDiscount = (subtotal - discount) * (appliedPromotion.value / 100);
      } else if (appliedPromotion.type === "fixed") {
        couponDiscount = Math.min(appliedPromotion.value, subtotal - discount);
      }
    }

    const total = subtotal - discount - couponDiscount;

    return {
      subtotal,
      discount,
      discountLabel,
      couponDiscount,
      couponLabel: appliedPromotion?.label || couponCode,
      total: Math.max(0, total),
      itemCount: cart.length
    };
  }, [cart, appliedPromotion, couponCode]);

  // Separate instant quote items from estimate items
  const categorizeItems = useCallback(() => {
    const instantQuoteItems = cart.filter(item => item.isInstantQuote);
    const estimateItems = cart.filter(item => !item.isInstantQuote);
    return { instantQuoteItems, estimateItems };
  }, [cart]);

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    couponCode,
    appliedPromotion,
    applyCoupon,
    removeCoupon,
    calculateTotals,
    categorizeItems,
    itemCount: cart.length
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
